import React, { useEffect, useMemo, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiX,
  FiCheck,
  FiArrowRight,
  FiRefreshCw,
  FiAlertCircle,
  FiPlus,
  FiEdit3,
  FiChevronDown,
  FiChevronUp
} = FiIcons;

const normalizeAccountName = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/&amp;/g, 'and')
    .replace(/&/g, 'and')
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const findMatchingProfitYoAccount = (waveAccount, profitYoAccounts) => {
  if (!waveAccount || !Array.isArray(profitYoAccounts)) {
    return null;
  }

  const waveName = normalizeAccountName(waveAccount.name);

  if (!waveName) {
    return null;
  }

  return profitYoAccounts.find(
    (account) => normalizeAccountName(account.name) === waveName
  ) || null;
};

export default function WaveImportModal({
  isOpen,
  onClose,
  accounts = [],
  organizationId,
  onImport,
  profitYoAccounts = [],
  loading = false,
  externalError = null
}) {
  const [step, setStep] = useState(1);
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [mappings, setMappings] = useState({});
  const [expandedAccountId, setExpandedAccountId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setStep(1);
    setSelectedAccountIds([]);
    setMappings({});
    setExpandedAccountId(null);
    setError('');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !accounts.length) {
      return;
    }

    const initialMappings = {};

    accounts.forEach((account) => {
      const match = findMatchingProfitYoAccount(account, profitYoAccounts);

      initialMappings[account.id] = {
        sourceAccountId: account.id,
        mode: match ? 'existing' : 'create',
        profitYoAccountId: match?.id || '',
        matchedAutomatically: Boolean(match)
      };
    });

    setMappings(initialMappings);
  }, [isOpen, accounts, profitYoAccounts]);

  const selectedAccounts = useMemo(
    () => accounts.filter((account) => selectedAccountIds.includes(account.id)),
    [accounts, selectedAccountIds]
  );

  const selectedMappings = useMemo(
    () =>
      selectedAccounts.map((account) =>
        mappings[account.id] || {
          sourceAccountId: account.id,
          mode: 'create',
          profitYoAccountId: '',
          matchedAutomatically: false
        }
      ),
    [selectedAccounts, mappings]
  );

  const matchedCount = selectedMappings.filter(
    (mapping) => mapping.mode === 'existing' && mapping.profitYoAccountId
  ).length;

  const createCount = selectedMappings.filter(
    (mapping) => mapping.mode === 'create'
  ).length;

  const needsMappingCount = selectedMappings.filter(
    (mapping) => mapping.mode === 'existing' && !mapping.profitYoAccountId
  ).length;

  const allSelected =
    accounts.length > 0 && selectedAccountIds.length === accounts.length;

  const toggleSelectAll = () => {
    setSelectedAccountIds(
      allSelected ? [] : accounts.map((account) => account.id)
    );
  };

  const toggleAccount = (accountId) => {
    setSelectedAccountIds((current) =>
      current.includes(accountId)
        ? current.filter((id) => id !== accountId)
        : [...current, accountId]
    );
  };

  const setAccountMode = (accountId, mode) => {
    setMappings((current) => ({
      ...current,
      [accountId]: {
        ...current[accountId],
        sourceAccountId: accountId,
        mode,
        profitYoAccountId:
          mode === 'create'
            ? ''
            : current[accountId]?.profitYoAccountId || '',
        matchedAutomatically: false
      }
    }));
  };

  const setExistingAccount = (accountId, profitYoAccountId) => {
    setMappings((current) => ({
      ...current,
      [accountId]: {
        ...current[accountId],
        sourceAccountId: accountId,
        mode: 'existing',
        profitYoAccountId,
        matchedAutomatically: false
      }
    }));
  };

  const handleContinue = () => {
    setError('');

    if (!selectedAccountIds.length) {
      setError('Please select at least one Wave account.');
      return;
    }

    setStep(2);
  };

  const handleBack = () => {
    setError('');
    setStep(1);
  };

  const handleImport = () => {
    setError('');

    if (!organizationId) {
      setError('No active organization was found.');
      return;
    }

    if (!selectedAccountIds.length) {
      setError('Please select at least one Wave account.');
      return;
    }

    const invalidMapping = selectedMappings.find(
      (mapping) => mapping.mode === 'existing' && !mapping.profitYoAccountId
    );

    if (invalidMapping) {
      setError(
        'Please select a ProfitYO account for every account marked "Map Existing".'
      );
      return;
    }

    onImport?.({
      organizationId,
      selectedAccountIds,
      selectedAccounts: selectedAccounts.map((account) => ({ ...account })),
      mappings: selectedMappings
    });
  };

  const handleClose = () => {
    if (!loading) {
      onClose?.();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-2xl">
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-100 px-6 py-5 sm:px-7">
          <div>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-violet-700">
                <span className="text-sm font-black">W</span>
              </div>

              <div>
                <h2 className="text-lg font-black tracking-tight text-slate-900">
                  Import from Wave
                </h2>
                <p className="mt-0.5 text-xs text-slate-500">
                  Select Wave accounts and map them to ProfitYO.
                </p>
              </div>
            </div>

            <div className="mt-5 flex items-center gap-2">
              <div
                className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-bold ${
                  step === 1
                    ? 'bg-slate-900 text-white'
                    : 'bg-emerald-50 text-emerald-700'
                }`}
              >
                {step > 1 ? (
                  <SafeIcon icon={FiCheck} className="text-xs" />
                ) : (
                  <span>1</span>
                )}
                Select Accounts
              </div>

              <SafeIcon
                icon={FiArrowRight}
                className="text-xs text-slate-300"
              />

              <div
                className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-bold ${
                  step === 2
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-400'
                }`}
              >
                <span>2</span>
                Review &amp; Map
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Close"
          >
            <SafeIcon icon={FiX} className="text-lg" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6 sm:px-7">
          {(externalError || error) && (
            <div className="mb-5 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              <SafeIcon
                icon={FiAlertCircle}
                className="mt-0.5 shrink-0 text-red-600"
              />
              <div>
                <p className="font-bold">Unable to continue</p>
                <p className="mt-1 leading-5">{error || externalError}</p>
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-black text-slate-900">
                    Wave Chart of Accounts
                  </h3>
                  <p className="mt-1 text-xs text-slate-500">
                    Select the accounts you want to bring into ProfitYO.
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-xs font-semibold text-slate-400">
                    {selectedAccountIds.length} of {accounts.length} selected
                  </span>
                  <button
                    type="button"
                    onClick={toggleSelectAll}
                    className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                  >
                    {allSelected ? 'Clear all' : 'Select all'}
                  </button>
                </div>
              </div>

              {accounts.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center">
                  <SafeIcon
                    icon={FiAlertCircle}
                    className="mx-auto text-2xl text-slate-400"
                  />
                  <p className="mt-3 text-sm font-bold text-slate-700">
                    No Wave accounts found
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    No accounts were returned from the connected Wave business.
                  </p>
                </div>
              ) : (
                <div className="overflow-hidden rounded-2xl border border-slate-200">
                  <div className="grid grid-cols-[auto_1fr_auto] items-center gap-4 border-b border-slate-100 bg-slate-50 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 rounded border-slate-300"
                    />
                    <span className="text-[11px] font-black uppercase tracking-wider text-slate-400">
                      Wave Account
                    </span>
                    <span className="text-[11px] font-black uppercase tracking-wider text-slate-400">
                      Type
                    </span>
                  </div>

                  <div className="divide-y divide-slate-100">
                    {accounts.map((account) => {
                      const selected = selectedAccountIds.includes(account.id);

                      return (
                        <label
                          key={account.id}
                          className={`grid cursor-pointer grid-cols-[auto_1fr_auto] items-center gap-4 px-4 py-4 transition ${
                            selected
                              ? 'bg-violet-50/40'
                              : 'bg-white hover:bg-slate-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleAccount(account.id)}
                            className="h-4 w-4 rounded border-slate-300"
                          />
                          <div className="min-w-0">
                            <p className="truncate text-sm font-bold text-slate-900">
                              {account.name}
                            </p>
                            <div className="mt-1 flex flex-wrap items-center gap-2">
                              {account.displayId && (
                                <span className="text-[11px] font-semibold text-slate-400">
                                  #{account.displayId}
                                </span>
                              )}
                              {account.description && (
                                <span className="truncate text-[11px] text-slate-400">
                                  {account.description}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-bold text-slate-600">
                            {account.typeName || account.type || 'Account'}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
                <div>
                  <h3 className="text-sm font-black text-slate-900">
                    Review Account Mapping
                  </h3>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    ProfitYO automatically matched accounts with the same name.
                    Review the mappings before importing.
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[11px] font-bold text-emerald-700">
                    {matchedCount} matched
                  </span>
                  <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-[11px] font-bold text-blue-700">
                    {createCount} create
                  </span>
                  {needsMappingCount > 0 && (
                    <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-[11px] font-bold text-amber-700">
                      {needsMappingCount} needs mapping
                    </span>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                {selectedAccounts.map((account) => {
                  const mapping = mappings[account.id] || {
                    sourceAccountId: account.id,
                    mode: 'create',
                    profitYoAccountId: '',
                    matchedAutomatically: false
                  };

                  const isExpanded = expandedAccountId === account.id;
                  const selectedProfitYoAccount = profitYoAccounts.find(
                    (item) => item.id === mapping.profitYoAccountId
                  );

                  return (
                    <div
                      key={account.id}
                      className="overflow-hidden rounded-2xl border border-slate-200 bg-white"
                    >
                      <div className="flex items-center gap-4 p-4">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-xs font-black text-violet-700">
                          W
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="truncate text-sm font-black text-slate-900">
                              {account.name}
                            </p>
                            {mapping.matchedAutomatically &&
                              mapping.mode === 'existing' && (
                                <span className="rounded-full bg-emerald-50 px-2 py-1 text-[9px] font-black uppercase tracking-wide text-emerald-700">
                                  Automatically matched
                                </span>
                              )}
                          </div>
                          <p className="mt-1 text-[11px] text-slate-400">
                            Source Account
                            {account.displayId ? ` • #${account.displayId}` : ''}
                            {account.typeName ? ` • ${account.typeName}` : ''}
                          </p>
                        </div>

                        <div className="hidden items-center gap-2 sm:flex">
                          {mapping.mode === 'existing' &&
                          mapping.profitYoAccountId ? (
                            <span className="rounded-full bg-emerald-50 px-3 py-1.5 text-[10px] font-bold text-emerald-700">
                              Matched
                            </span>
                          ) : (
                            <span className="rounded-full bg-blue-50 px-3 py-1.5 text-[10px] font-bold text-blue-700">
                              Create account
                            </span>
                          )}
                        </div>

                        <button
                          type="button"
                          onClick={() =>
                            setExpandedAccountId(isExpanded ? null : account.id)
                          }
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                          aria-label={`Toggle mapping for ${account.name}`}
                        >
                          <SafeIcon
                            icon={isExpanded ? FiChevronUp : FiChevronDown}
                          />
                        </button>
                      </div>

                      {isExpanded && (
                        <div className="border-t border-slate-100 bg-slate-50/70 p-4">
                          <div className="grid gap-3 sm:grid-cols-2">
                            <button
                              type="button"
                              onClick={() =>
                                setAccountMode(account.id, 'create')
                              }
                              className={`rounded-2xl border p-4 text-left transition ${
                                mapping.mode === 'create'
                                  ? 'border-blue-300 bg-blue-50'
                                  : 'border-slate-200 bg-white hover:border-slate-300'
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <div
                                  className={`flex h-9 w-9 items-center justify-center rounded-xl ${
                                    mapping.mode === 'create'
                                      ? 'bg-blue-100 text-blue-700'
                                      : 'bg-slate-100 text-slate-500'
                                  }`}
                                >
                                  <SafeIcon icon={FiPlus} />
                                </div>
                                <div>
                                  <p className="text-xs font-black text-slate-900">
                                    Create Account
                                  </p>
                                  <p className="mt-1 text-[11px] leading-5 text-slate-500">
                                    Create a new ProfitYO account based on this
                                    Wave account.
                                  </p>
                                </div>
                              </div>
                            </button>

                            <button
                              type="button"
                              onClick={() =>
                                setAccountMode(account.id, 'existing')
                              }
                              className={`rounded-2xl border p-4 text-left transition ${
                                mapping.mode === 'existing'
                                  ? 'border-emerald-300 bg-emerald-50'
                                  : 'border-slate-200 bg-white hover:border-slate-300'
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <div
                                  className={`flex h-9 w-9 items-center justify-center rounded-xl ${
                                    mapping.mode === 'existing'
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : 'bg-slate-100 text-slate-500'
                                  }`}
                                >
                                  <SafeIcon icon={FiEdit3} />
                                </div>
                                <div>
                                  <p className="text-xs font-black text-slate-900">
                                    Map Existing
                                  </p>
                                  <p className="mt-1 text-[11px] leading-5 text-slate-500">
                                    Connect this Wave account to an existing
                                    ProfitYO account.
                                  </p>
                                </div>
                              </div>
                            </button>
                          </div>

                          {mapping.mode === 'existing' && (
                            <div className="mt-4">
                              <label
                                className="mb-2 block text-[11px] font-black uppercase tracking-wide text-slate-500"
                                htmlFor={`wave-account-${account.id}`}
                              >
                                ProfitYO Account
                              </label>

                              {profitYoAccounts.length === 0 ? (
                                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs font-semibold text-amber-800">
                                  No existing ProfitYO accounts are available to
                                  map.
                                </div>
                              ) : (
                                <select
                                  id={`wave-account-${account.id}`}
                                  value={mapping.profitYoAccountId || ''}
                                  onChange={(event) =>
                                    setExistingAccount(
                                      account.id,
                                      event.target.value
                                    )
                                  }
                                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-900/5"
                                >
                                  <option value="">
                                    Select a ProfitYO account
                                  </option>
                                  {profitYoAccounts.map((profitYoAccount) => (
                                    <option
                                      key={profitYoAccount.id}
                                      value={profitYoAccount.id}
                                    >
                                      {profitYoAccount.name}
                                      {profitYoAccount.code
                                        ? ` (${profitYoAccount.code})`
                                        : ''}
                                    </option>
                                  ))}
                                </select>
                              )}

                              {selectedProfitYoAccount && (
                                <p className="mt-2 text-[11px] font-semibold text-emerald-700">
                                  Wave "{account.name}" will use ProfitYO "
                                  {selectedProfitYoAccount.name}".
                                </p>
                              )}
                            </div>
                          )}

                          <div className="mt-4 rounded-xl border border-slate-200 bg-white p-3">
                            <p className="text-[10px] font-black uppercase tracking-wide text-slate-400">
                              Source Account
                            </p>
                            <div className="mt-2 flex flex-wrap gap-2">
                              <span className="rounded-lg bg-slate-100 px-2.5 py-1.5 text-[11px] font-bold text-slate-600">
                                {account.name}
                              </span>
                              {account.displayId && (
                                <span className="rounded-lg bg-slate-100 px-2.5 py-1.5 text-[11px] font-bold text-slate-600">
                                  #{account.displayId}
                                </span>
                              )}
                              {account.typeName && (
                                <span className="rounded-lg bg-slate-100 px-2.5 py-1.5 text-[11px] font-bold text-slate-600">
                                  {account.typeName}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-slate-100 bg-white px-6 py-4 sm:px-7">
          <div>
            {step === 1 ? (
              <span className="text-xs font-semibold text-slate-400">
                {selectedAccountIds.length} account
                {selectedAccountIds.length === 1 ? '' : 's'} selected
              </span>
            ) : (
              <span className="text-xs font-semibold text-slate-400">
                {selectedAccounts.length} account
                {selectedAccounts.length === 1 ? '' : 's'} ready to import
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={step === 1 ? handleClose : handleBack}
              disabled={loading}
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-xs font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {step === 1 ? 'Cancel' : 'Back'}
            </button>

            {step === 1 ? (
              <button
                type="button"
                onClick={handleContinue}
                disabled={!selectedAccountIds.length}
                className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-xs font-bold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Continue
                <SafeIcon icon={FiArrowRight} />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleImport}
                disabled={
                  loading ||
                  !selectedAccountIds.length ||
                  needsMappingCount > 0
                }
                className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-xs font-bold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-wait disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <SafeIcon icon={FiRefreshCw} className="animate-spin" />
                    Importing…
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiCheck} />
                    Import Accounts
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}